import { OsmWayEnhancedRequester } from '@cobliteam/api-lib';
import type {
  OsmGeoJsonFeature,
  OsmWayEnhancedByGeoJsonRequesterParams,
  OsmWayEnhancedHistoryItem,
  OsmWayEnhancedItem,
  OsmWayEnhancedPaginatedResponse,
  OsmWayEnhancedUpsertPayload,
} from '@cobliteam/api-lib/src/dashboard/osmWayEnhanced/APITypes';
import { Environment } from '@cobliteam/react-environments';
import { Alert } from '@cobliteam/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseGetOsmWayByIdParams, UseGetOsmWayEnhancedHistoryByWayIdParams, UseGetOsmWayEnhancedNearestParams, UseGetOsmWaysPaginatedParams } from './types';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getOsmWayEnhancedRequester = () => new OsmWayEnhancedRequester({ host: env.apiHost });

export const QUERY_KEY = ['OSM_WAY_ENHANCED'];
export const QUERY_KEY_BY_ID = [...QUERY_KEY, 'BY_ID'];
export const QUERY_KEY_PAGINATED = [...QUERY_KEY, 'PAGINATED'];
export const QUERY_KEY_NEAREST = [...QUERY_KEY, 'NEAREST'];
export const QUERY_KEY_BY_GEOJSON = [...QUERY_KEY, 'BY_GEOJSON'];
export const QUERY_KEY_HISTORY_BY_WAY_ID = [...QUERY_KEY, 'HISTORY_BY_WAY_ID'];

export const useGetOsmWayEnhancedNearest = ({ params, options }: UseGetOsmWayEnhancedNearestParams) => {
  return useQuery<OsmGeoJsonFeature, Error, OsmGeoJsonFeature>({
    queryKey: [...QUERY_KEY_NEAREST, params],
    queryFn: () => getOsmWayEnhancedRequester().getOsmWayEnhancedNearest(params),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    ...options,
  });
};

export const useGetOsmWayEnhancedByBounds = ({ geoJson, enabled }: OsmWayEnhancedByGeoJsonRequesterParams) => {
  return useQuery<OsmGeoJsonFeature[], Error>({
    enabled,
    queryKey: [...QUERY_KEY_BY_GEOJSON, { geoJson }],
    queryFn: () => getOsmWayEnhancedRequester().getOsmWayEnhancedByGeoJson({ geoJson }),
  });
};

export const useUpsertOsmWayEnhanced = () => {
  const queryClient = useQueryClient();
  const toastId = 'road-speed-form';
  return useMutation<number, Error, OsmWayEnhancedUpsertPayload[]>({
    mutationFn: (data: OsmWayEnhancedUpsertPayload[]) => getOsmWayEnhancedRequester().upsertOsmWayEnhanced(data),
    onMutate: () => {
      Alert.loading('Salvando velocidade da via...', {
        id: toastId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY_BY_ID] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY_NEAREST] });
      Alert.success('Velocidade da via salva com sucesso', {
        id: toastId,
      });
    },
    onError: (error) => {
      Alert.error('Erro ao salvar ou atualizar velocidade da via', {
        description: error.message,
        id: toastId,
      });
    },
  });
};

export const useGetOsmWaysPaginated = ({ params, options }: UseGetOsmWaysPaginatedParams) => {
  return useQuery<OsmWayEnhancedPaginatedResponse, Error, OsmWayEnhancedPaginatedResponse>({
    queryKey: [...QUERY_KEY_PAGINATED, params],
    queryFn: () => getOsmWayEnhancedRequester().getOsmWaysPaginated(params),
    ...options,
  });
};

export const useGetOsmWayById = ({ params, options }: UseGetOsmWayByIdParams) => {
  return useQuery<OsmWayEnhancedItem, Error, OsmWayEnhancedItem>({
    queryKey: [...QUERY_KEY_BY_ID, params],
    queryFn: () => getOsmWayEnhancedRequester().getOsmWayEnhancedById({ wayId: params.wayId }),
    ...options,
  });
};

export const useInactivateOsmWayEnhanced = () => {
  const queryClient = useQueryClient();
  const toastId = 'road-speed-inactivate-form';
  return useMutation<number, Error, OsmWayEnhancedUpsertPayload[]>({
    mutationFn: (data: OsmWayEnhancedUpsertPayload[]) => getOsmWayEnhancedRequester().upsertOsmWayEnhanced(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY] });
      Alert.success('Velocidade da via desativada com sucesso', {
        id: toastId,
      });
    },
    onError: (error) => {
      Alert.error('Erro ao desativar velocidade da via', {
        description: error.message,
        id: toastId,
      });
    },
  });
};

export const useGetOsmWayEnhancedHistoryByWayId = ({ params, options }: UseGetOsmWayEnhancedHistoryByWayIdParams) => {
  return useQuery<OsmWayEnhancedHistoryItem[], Error, OsmWayEnhancedHistoryItem[]>({
    queryKey: [...QUERY_KEY_HISTORY_BY_WAY_ID, params],
    queryFn: () => getOsmWayEnhancedRequester().getOsmWayEnhancedHistoryByWayId(params),
    ...options,
  });
};
